import { useCallback, useEffect, useState } from 'react';

/**
 * Data the splash fetches ahead of time, so a screen opens already filled.
 *
 * A screen reads its entry synchronously on first render (no spinner frame
 * when the splash got there first), then refreshes in the background if the
 * entry is older than `STALE_MS`. Writes that change what a screen shows
 * (a new coach message) go through the setter, which updates the entry too —
 * leaving and coming back never shows an older copy than the screen had.
 *
 * In memory only, per app run; cleared on sign-out.
 */
const STALE_MS = 30_000;

interface Entry<T> {
  value?: T;
  error?: unknown;
  at: number;
  inflight?: Promise<T>;
}

const entries = new Map<string, Entry<unknown>>();

/**
 * A loader result that is worth showing but not worth trusting — rows from the
 * device cache because the server was unreachable. Stored already stale, so
 * the next read fetches again.
 */
class Provisional<T> {
  constructor(readonly value: T) {}
}
export function provisional<T>(value: T): T {
  return new Provisional(value) as unknown as T;
}

/** Start (or join) a fetch for `key`. Never rejects; failures are stored. */
export function warm<T>(key: string, load: () => Promise<T>, maxAgeMs = STALE_MS): Promise<T | undefined> {
  const entry = entries.get(key) as Entry<T> | undefined;
  if (entry?.inflight) return entry.inflight.catch(() => undefined);
  if (entry && 'value' in entry && Date.now() - entry.at < maxAgeMs) return Promise.resolve(entry.value);

  const inflight = load().then((result) =>
    result instanceof Provisional ? (result as Provisional<T>) : result,
  );
  entries.set(key, { ...entry, at: entry?.at ?? 0, inflight: inflight as Promise<T> });
  return inflight
    .then((result) => {
      if (result instanceof Provisional) {
        const value = (result as Provisional<T>).value;
        entries.set(key, { value, at: 0 });
        return value;
      }
      entries.set(key, { value: result as T, at: Date.now() });
      return result as T;
    })
    .catch((error: unknown) => {
      entries.set(key, { ...(entries.get(key) as Entry<T>), error, inflight: undefined });
      return undefined;
    });
}

/** Why the last fetch for `key` failed, if it did. */
export function lastError(key: string): unknown {
  return entries.get(key)?.error;
}

export function peek<T>(key: string): T | undefined {
  return (entries.get(key) as Entry<T> | undefined)?.value;
}

export function put<T>(key: string, value: T): void {
  entries.set(key, { value, at: Date.now() });
}

/**
 * Keep showing what is cached, but refetch on the next read — for data the
 * user just changed elsewhere (a session saved while Progress was cached).
 */
export function markStale(prefix: string): void {
  for (const [key, entry] of entries) {
    if (key.startsWith(prefix)) entries.set(key, { ...entry, at: 0 });
  }
}

export function clearWarmCache(): void {
  entries.clear();
}

export type WarmState<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'failed'; message: string };

/**
 * A screen's view of one entry. `key` null means "not yet" (no user); the
 * state stays `loading` and nothing is fetched.
 */
export function useWarm<T>(
  key: string | null,
  load: () => Promise<T>,
  failMessage: string,
): [WarmState<T>, (next: T) => void, () => void] {
  const initial = (): WarmState<T> => {
    if (!key) return { status: 'loading' };
    const value = peek<T>(key);
    return value === undefined ? { status: 'loading' } : { status: 'ready', data: value };
  };
  const [state, setState] = useState<WarmState<T>>(initial);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    void warm(key, load).then((value) => {
      if (cancelled) return;
      if (value !== undefined) {
        setState({ status: 'ready', data: value });
      } else if (peek<T>(key) === undefined) {
        const error = lastError(key);
        setState({ status: 'failed', message: error instanceof Error ? error.message : failMessage });
      }
    });
    return () => {
      cancelled = true;
    };
    // `load` is a fresh closure every render; the key identifies the data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  /** Fetch again after a failure, without reloading the app. */
  const retry = useCallback(() => {
    if (!key) return;
    markStale(key);
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, [key]);

  const update = useCallback(
    (next: T) => {
      if (key) put(key, next);
      setState({ status: 'ready', data: next });
    },
    [key],
  );

  return [state, update, retry];
}
